import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission, isOwner, isOwnerOrAdmin } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, ShieldCheck, Users as UsersIcon, Stethoscope, ClipboardList, Archive, Undo2, Filter, X, Trash2, Search, ChevronRight, ChevronDown, Layers, MailWarning } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import BrandRolesInline from "./BrandRoles";
import BrandStaffWizard from "./BrandStaffWizard";
import ReportPermissionsDialog from "./ReportPermissionsDialog";
import CommissionTiersTab from "./CommissionTiersTab";

const empty = { full_name: "", email: "", role: "staff", role_id: "", branch_ids: [] };

/**
 * Brand · Staff Management page.
 *
 * Two tabs live here: **Staff Logins** (this file's original scope) and
 * **Roles** (rendered via `<BrandRolesInline />`). The active tab is
 * kept in the URL as `?tab=logins|roles` so we can deep-link and the
 * sidebar can push straight into either view.
 */
export default function BrandUsers() {
  const { staff } = useAuth();
  const { search } = useLocation();
  const nav = useNavigate();

  const params = new URLSearchParams(search);
  const TABS = ["logins", "roles", "commission-tiers"];
  const readTab = (sp) => {
    const t = sp.get("tab");
    return TABS.includes(t) ? t : "logins";
  };
  const [tab, setTab] = useState(() => readTab(params));
  useEffect(() => { setTab(readTab(new URLSearchParams(search))); }, [search]);

  const setTabSynced = (v) => {
    setTab(v);
    const sp = new URLSearchParams(search);
    if (v === "logins") sp.delete("tab"); else sp.set("tab", v);
    nav({ search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
  };

  // Roles tab is strictly Owner-only (Feb 8 2026) — even Admin logins
  // with `roles.manage_roles=true` in their permission map can't see or
  // click into it, because role authoring is a system-level surface.
  const canManageRoles = isOwner(staff);
  // Commission Tiers mirrors the standard commission editor's gate —
  // Owner or anyone holding `employees.edit_staff`.
  const canManageCommission = isOwner(staff) || hasPermission(staff, "employees.edit_staff");

  return (
    <StaffShell context="brand">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">Staff Management</h1>
      </div>
      <Tabs value={tab} onValueChange={setTabSynced}>
        <TabsList data-testid="staff-tabs">
          <TabsTrigger value="logins" data-testid="staff-tab-logins" className="gap-2"><UsersIcon className="w-4 h-4" /> Staff Logins</TabsTrigger>
          {canManageRoles && (
            <TabsTrigger value="roles" data-testid="staff-tab-roles" className="gap-2"><ShieldCheck className="w-4 h-4" /> Roles</TabsTrigger>
          )}
          {canManageCommission && (
            <TabsTrigger value="commission-tiers" data-testid="staff-tab-commission-tiers" className="gap-2"><Layers className="w-4 h-4" /> Commission Tiers</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="logins" className="mt-4">
          <StaffLoginsTab />
        </TabsContent>
        {canManageRoles && (
          <TabsContent value="roles" className="mt-4">
            <BrandRolesInline />
          </TabsContent>
        )}
        {canManageCommission && (
          <TabsContent value="commission-tiers" className="mt-4">
            <CommissionTiersTab />
          </TabsContent>
        )}
      </Tabs>
    </StaffShell>
  );
}

function SectionCard({ title, icon, desc, testid, list, renderRows, showReportColumn }) {
  // Collapsed-by-default per-group with session-scoped persistence.
  // Fresh page loads always start collapsed (sessionStorage is wiped on
  // tab close). Within a session, expanding a group and navigating away
  // then back preserves the expanded state. Keyed on `testid` so each
  // group (Management, Care Team, Archived, …) tracks independently.
  const storageKey = `brandUsers.sectionOpen.${testid}`;
  const [open, setOpen] = React.useState(() => {
    try { return sessionStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { sessionStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <Card className="rounded-lg" data-testid={testid}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="w-full px-4 py-3 border-b border-border flex items-center gap-2 text-left hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid={`${testid}-toggle`}
        >
          {open
            ? <ChevronDown className="w-4 h-4 text-muted-foreground" data-testid={`${testid}-chevron-open`} />
            : <ChevronRight className="w-4 h-4 text-muted-foreground" data-testid={`${testid}-chevron-closed`} />
          }
          {icon}
          <h3 className="text-sm font-semibold">{title}</h3>
          <span className="text-xs text-muted-foreground" data-testid={`${testid}-count`}>({list.length})</span>
          {desc && <span className="text-xs text-muted-foreground ml-2">· {desc}</span>}
        </button>
        {open && (
          list.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground text-center">No members in this group.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Whatsapp</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Branch</TableHead>
                  {showReportColumn && <TableHead>Report</TableHead>}
                  <TableHead>Last Login</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderRows(list)}</TableBody>
            </Table>
          )
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Archived / Inactive section. Shares the same collapsed-by-default
 * behaviour as `SectionCard` but keeps the inline "Select All & Delete"
 * owner action pinned to the header row — the chevron toggles only the
 * body, so the purge button stays reachable in both states.
 */
function ArchivedSectionCard({ list, isOwner, onPurge, renderRows }) {
  const storageKey = "brandUsers.sectionOpen.section-archived";
  const [open, setOpen] = React.useState(() => {
    try { return sessionStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { sessionStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <Card className="rounded-lg border-dashed" data-testid="section-archived">
      <CardContent className="p-0">
        <div className="w-full px-4 py-3 border-b border-border flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="flex-1 flex items-center gap-2 text-left hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded-sm"
            data-testid="section-archived-toggle"
          >
            {open
              ? <ChevronDown className="w-4 h-4 text-muted-foreground" data-testid="section-archived-chevron-open" />
              : <ChevronRight className="w-4 h-4 text-muted-foreground" data-testid="section-archived-chevron-closed" />
            }
            <Archive className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Archived / Inactive</h3>
            <span className="text-xs text-muted-foreground" data-testid="section-archived-count">({list.length})</span>
            <span className="text-xs text-muted-foreground ml-2">· Login disabled — reactivate any time</span>
          </button>
          {isOwner && list.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 gap-1 h-8"
              data-testid="purge-inactive-btn"
              onClick={onPurge}
            >
              <Trash2 className="w-3.5 h-3.5" /> Select All &amp; Permanently Delete
            </Button>
          )}
        </div>
        {open && (
          list.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground text-center" data-testid="archived-empty">Nobody is archived — everyone is currently active.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Whatsapp</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderRows(list)}</TableBody>
            </Table>
          )
        )}
      </CardContent>
    </Card>
  );
}

function BranchFilter({ branches, selected, onChange }) {
  const count = selected.size;
  const clearAll = (e) => { e?.stopPropagation(); onChange(new Set()); };
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };
  const label = count === 0
    ? "All branches"
    : count === 1
      ? branches.find((b) => selected.has(b.id))?.name || "1 branch"
      : `${count} branches`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 rounded-full"
          data-testid="branch-filter-trigger"
        >
          <Filter className="w-3.5 h-3.5" />
          <span className="text-xs">Branch: {label}</span>
          {count > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearAll}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearAll(e); }}
              className="text-muted-foreground hover:text-foreground rounded-full inline-flex items-center"
              data-testid="branch-filter-clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid="branch-filter-popover">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between text-xs">
          <span className="font-medium">Filter by branch</span>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => onChange(new Set())}
            data-testid="branch-filter-all"
          >
            Show all
          </button>
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {branches.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-4 text-center">No branches yet.</p>
          )}
          {branches.map((b) => {
            const on = selected.has(b.id);
            return (
              <label
                key={b.id}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted text-sm"
                data-testid={`branch-filter-option-${b.id}`}
              >
                <Checkbox checked={on} onCheckedChange={() => toggle(b.id)} data-testid={`branch-filter-check-${b.id}`} />
                <span className="min-w-0 truncate">{b.name}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StaffLoginsTab() {
  const { staff } = useAuth();
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Category filters
  const [showInactive, setShowInactive] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeAck, setPurgeAck] = useState(false);
  const [purging, setPurging] = useState(false);
  // Multi-select branch filter — empty set == "All branches". Sorting
  // + filtering both run on the client for zero-latency response as
  // the operator toggles branches on/off.
  const [branchFilterIds, setBranchFilterIds] = useState(() => new Set());
  // Free-text search across full_name / username / email. Runs
  // client-side over the already-loaded roster so the results appear
  // as the operator types, with zero round-trips.
  const [query, setQuery] = useState("");
  // "Can't sign in" filter — employees authenticate with a code emailed to
  // the address on their own profile, so a blank email means a locked-out
  // team member. This narrows the roster to exactly those people.
  const [missingEmailOnly, setMissingEmailOnly] = useState(false);
  // Reactivate dialog — { user, branch_ids: Set }
  const [reactivateCtx, setReactivateCtx] = useState(null);
  // Report Permissions overlay ctx — { user, scope: "brand"|"branch" }.
  const [reportPermCtx, setReportPermCtx] = useState(null);

  const canCreate = hasPermission(staff, "employees.create_staff");
  const canEdit = hasPermission(staff, "employees.edit_staff");
  const actorLevel = staff?.hierarchy_level || 0;

  const load = useCallback(async () => {
    try {
      const [u, b, r, c, s] = await Promise.all([
        // Bust caches (browser, CDN, mobile Safari) so the toggle flip
        // always sees the LIVE registry — no more "ghost" archived rows
        // lingering after a purge or reactivate.
        staffApi.get(`/staff-users?_ts=${Date.now()}`),
        staffApi.get("/branches"),
        staffApi.get("/roles").catch(() => ({ data: [] })),
        staffApi.get("/categories").catch(() => ({ data: [] })),
        staffApi.get("/services").catch(() => ({ data: [] })),
      ]);
      setRows(u.data);
      setBranches(b.data);
      setRoles(r.data || []);
      setCategories(c.data || []);
      setServices(s.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Re-fetch the roster whenever the operator flips the "Show Inactive
  // Users" switch. The switch is a hard mode change (Active-only ↔
  // Inactive-only) so stale data must not linger between views — this
  // guarantees the visible rows always reflect the LIVE DB state.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const rolesForAssignment = roles.filter((r) => (isOwner(staff) || (!r.is_owner && r.hierarchy_level <= actorLevel)));
  const rolesById = Object.fromEntries(roles.map((r) => [r.id, r]));
  // Iter-206 — Report Permissions column is Owner + Admin only. Managers,
  // Supervisors, Reservation, Care Team all get the whole column hidden.
  const canManageReportPerms = isOwnerOrAdmin(staff);

  // Care Team vs Management is driven strictly by the user's assigned
  // Role, NOT by whether they hold service_ids (an ops mistake can
  // leave services on a Manager profile). Administrative preset roles
  // — Owner, Admin, Manager, Supervisor, Reservation, Store — always
  // list under "Active Management Team". Any other role (Care Team
  // preset, legacy Staff default, custom low-hierarchy role) lists
  // under "Active Care Team" and appears on the booking calendar.
  const MANAGEMENT_ROLE_IDS = new Set([
    "role_owner",
    "role_admin",
    "role_preset_manager",
    "role_preset_supervisor",
    "role_preset_reservation",
    "role_preset_store",
  ]);
  const isCareTeam = (u) => {    const r = rolesById[u.role_id];
    if (!r) return false; // unknown role → stay in Management for safety
    if (r.is_owner || r.is_locked) return false;
    return !MANAGEMENT_ROLE_IDS.has(r.id);
  };
  // Active employees who cannot receive a sign-in code: no address at all,
  // or a value that isn't an email (several legacy rows hold a name or a
  // username in the `email` field). Counted off the raw roster so the
  // banner stays accurate while the operator filters the table.
  const canSignIn = (u) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((u.email || "").trim());
  const lockedOutCount = rows.filter((u) => u.active !== false && !canSignIn(u)).length;

  const groups = React.useMemo(() => {
    // Branch filter (empty set == "All branches"). Alphabetical A→Z on
    // full_name, case-insensitive, uses `localeCompare` so accented
    // names (Bali/Indonesian) sort naturally.
    const branchFilter = branchFilterIds;
    const q = query.trim().toLowerCase();
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const passesBranchFilter = (u) => {
      if (branchFilter.size === 0) return true;
      const ids = u.branch_ids || [];
      // Owners hold the empty branch set == "all branches"; treat them
      // as always matching so they don't disappear when a manager
      // narrows the filter to a single branch.
      const roleIsOwner = rolesById[u.role_id]?.is_owner || u.role === "brand_admin";
      if (roleIsOwner && ids.length === 0) return true;
      for (const b of ids) if (branchFilter.has(b)) return true;
      return false;
    };
    // Case-insensitive substring across name / username / email so a
    // manager typing "sam" surfaces "Samantha", "samuel@…" and the
    // username "samw" in one pass.
    const passesQuery = (u) => {
      if (!q) return true;
      return (
        (u.full_name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
      );
    };
    const g = { management: [], careTeam: [], archived: [] };
    for (const u of rows) {
      if (!passesBranchFilter(u)) continue;
      if (!passesQuery(u)) continue;
      if (missingEmailOnly && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((u.email || "").trim())) continue;
      if (u.active === false) g.archived.push(u);
      else if (isCareTeam(u)) g.careTeam.push(u);
      else g.management.push(u);
    }
    const alpha = (a, b) => collator.compare(a.full_name || "", b.full_name || "");
    g.management.sort(alpha);
    g.careTeam.sort(alpha);
    g.archived.sort(alpha);
    return g;
  }, [rows, roles, branchFilterIds, rolesById, query, missingEmailOnly]);

  // Whenever the archived list becomes empty (last purge, all reactivated)
  // collapse the toggle back to OFF so the archived section can't
  // accidentally linger on-screen empty and confuse the operator.
  useEffect(() => {
    if (groups.archived.length === 0 && showInactive && rows.length > 0) {
      setShowInactive(false);
    }
  }, [groups.archived.length, showInactive, rows.length]);

  const openNew = () => {
    if (!canCreate) return;
    setWizardOpen(true);
  };
  const openEdit = (u) => {
    if (!canEdit) return;
    setEditing(u);
    setWizardOpen(true);
  };

  /** One-tap Active/Inactive flip from the table row — bypasses the
   *  full editor. Uses the same PUT /staff-users/{id} endpoint the
   *  wizard uses, so the calendar and schedule pages pick up the
   *  change on their next fetch (both queries filter by
   *  `active: true`). Deactivating never touches the person's
   *  service_ids or schedule; reactivation is preserved intact.
   */
  const toggleActive = async (u, next) => {
    if (!canEdit) return;
    // Prevent operator from disabling their own login by accident
    if (u.id === staff?.id && !next) {
      toast.error("You can't deactivate your own login.");
      return;
    }
    try {
      await staffApi.put(`/staff-users/${u.id}`, { active: next });
      toast.success(`${u.full_name} is now ${next ? "Active" : "Inactive"}.`);
      setRows((prev) => prev.map((r) => (r.id === u.id ? { ...r, active: next } : r)));
    } catch (err) {
      toast.error(formatApiError(err));
      // Refetch to snap the UI back if the server rejected the change
      load();
    }
  };

  const openReactivate = (u) => setReactivateCtx({ user: u, branch_ids: new Set() });
  const submitReactivate = async () => {
    if (!reactivateCtx) return;
    const ids = [...reactivateCtx.branch_ids];
    if (ids.length === 0) { toast.error("Pick at least one branch to reactivate."); return; }
    try {
      await staffApi.post(`/staff-users/${reactivateCtx.user.id}/reactivate`, { branch_ids: ids });
      toast.success(`${reactivateCtx.user.full_name} reactivated — login restored`);
      setReactivateCtx(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const branchNames = (u) => {
    const ids = u.branch_ids || [];
    const roleIsOwner = rolesById[u.role_id]?.is_owner || u.role === "brand_admin";
    if (ids.length === 0) return roleIsOwner ? "All (Owner)" : "— None assigned";
    return ids.map((id) => branches.find((b) => b.id === id)?.name).filter(Boolean).join(", ");
  };

  // ISO-8601 UTC → local `DD/MM/YY HH:MM`. Falls back to an em-dash so
  // the column reads cleanly for accounts that have never signed in
  // (freshly-provisioned staff, seed rows) instead of showing "Invalid Date".
  const formatLastLogin = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Row renderer. `sectionAllowsReportColumn` gates the per-user Report
  // cell — set true only for the Management section so Care Team stays
  // free of the column entirely (iter-209).
  const renderRows = (list, sectionAllowsReportColumn = false) => list.map((u) => {
    const roleIsOwner = rolesById[u.role_id]?.is_owner || u.role === "brand_admin";
    const showReportCell = canManageReportPerms && sectionAllowsReportColumn;
    return (
    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
      <TableCell className="font-medium">{u.full_name}</TableCell>
      <TableCell className="text-xs" data-testid={`user-email-${u.id}`}>
        {!(u.email || "").trim()
          ? <span className="text-destructive">no email — cannot sign in</span>
          : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((u.email || "").trim())
            ? <a href={`mailto:${u.email}`} className="text-primary hover:underline break-all">{u.email}</a>
            : <span className="text-destructive break-all" data-testid={`user-email-invalid-${u.id}`}>{u.email} — not an email, cannot sign in</span>}
      </TableCell>
      <TableCell className="text-xs" data-testid={`user-whatsapp-${u.id}`}>
        {u.whatsapp
          ? <span className="tabular-nums">{u.whatsapp}</span>
          : <span className="opacity-50">—</span>}
      </TableCell>
      <TableCell><Badge variant="outline" className="rounded-full text-[10px]" data-testid={`user-role-${u.id}`}>{rolesById[u.role_id]?.name || u.role}</Badge></TableCell>
      <TableCell className="text-xs" data-testid={`user-branches-${u.id}`}>{branchNames(u)}</TableCell>
      {showReportCell && (
        <TableCell className="text-xs" data-testid={`user-report-perms-${u.id}`}>
          {/* Owner is intentionally uneditable — surfacing a muted "Owner"
              hint keeps the column tidy and communicates the security rule.
              Everyone else gets two side-by-side links that pop the
              permission overlay for the chosen scope. */}
          {roleIsOwner ? (
            <span className="italic text-muted-foreground" data-testid={`user-report-perms-owner-${u.id}`}>Owner · all</span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setReportPermCtx({ user: u, scope: "brand" })}
                data-testid={`user-report-perms-brand-${u.id}`}
              >Brand</button>
              <span className="opacity-40">·</span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setReportPermCtx({ user: u, scope: "branch" })}
                data-testid={`user-report-perms-branch-${u.id}`}
              >Branch</button>
            </span>
          )}
        </TableCell>
      )}
      <TableCell className="text-xs font-mono tabular-nums" data-testid={`user-last-login-${u.id}`}>{formatLastLogin(u.last_login_at)}</TableCell>
      <TableCell className="text-xs">
        {u.active === false ? (
          <span className="text-muted-foreground italic" data-testid={`user-status-label-${u.id}`}>Inactive</span>
        ) : (
          <label className="inline-flex items-center gap-2 cursor-pointer" title="Toggle Active / Inactive">
            <Switch
              checked={!!u.active}
              onCheckedChange={(v) => toggleActive(u, !!v)}
              data-testid={`user-status-toggle-${u.id}`}
              disabled={!canEdit || u.id === staff?.id}
            />
            <span data-testid={`user-status-label-${u.id}`}>Active</span>
          </label>
        )}
      </TableCell>
      <TableCell className="text-right">
        {u.active === false ? (
          canEdit && (
            <Button size="sm" variant="outline" onClick={() => openReactivate(u)} data-testid={`reactivate-user-${u.id}`}>
              <Undo2 className="w-3.5 h-3.5 mr-1" /> Reactivate
            </Button>
          )
        ) : (
          canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
        )}
      </TableCell>
    </TableRow>
    );
  });

  return (
    <div>
      {lockedOutCount > 0 && !showInactive && (
        <Card className="rounded-lg mb-4 border-amber-300 bg-amber-50" data-testid="staff-missing-email-banner">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <MailWarning className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900" data-testid="staff-missing-email-count">
                {lockedOutCount} active {lockedOutCount === 1 ? "employee cannot" : "employees cannot"} sign in — their profile has no email, or a value that isn&rsquo;t an email address
              </p>
              <p className="text-[11px] text-amber-800/80">
                Everyone signs in with a code emailed to the address on their own profile. Add it under Edit and they can sign in straight away.
              </p>
            </div>
            <Button
              size="sm"
              variant={missingEmailOnly ? "default" : "outline"}
              className="h-8 ml-auto"
              onClick={() => setMissingEmailOnly((v) => !v)}
              data-testid="staff-missing-email-filter-btn"
            >
              {missingEmailOnly ? "Show everyone" : "Show only these"}
            </Button>
          </CardContent>
        </Card>
      )}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Headcount pill — LABEL AND COUNT switch based on the
           *  toggle so the number always matches the rows visible on
           *  screen (Active-only ↔ Inactive-only mutual exclusivity). */}
          <div className="flex items-center gap-2" data-testid="active-total">
            <span className="text-sm font-medium">{showInactive ? "Inactive users:" : "Active team:"}</span>
            <Badge
              className={
                showInactive
                  ? "rounded-full bg-slate-100 text-slate-800 border-slate-200"
                  : "rounded-full bg-emerald-100 text-emerald-900 border-emerald-200"
              }
              data-testid="active-total-count"
            >
              {showInactive
                ? `${groups.archived.length} people`
                : `${groups.management.length + groups.careTeam.length} people`}
            </Badge>
          </div>
          {/* Toggle is ALWAYS rendered so the operator can enter
           *  Inactive-only view even from a fully-clean registry (the
           *  empty state inside the archived card explains the zero).
           *  When any archived rows exist the "(N archived)" hint
           *  surfaces the count. */}
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
            <span>Show Inactive Users</span>
            {groups.archived.length > 0 && (
              <span className="text-xs text-muted-foreground" data-testid="inactive-count-hint">
                ({groups.archived.length} archived)
              </span>
            )}
          </label>
          <BranchFilter
            branches={branches}
            selected={branchFilterIds}
            onChange={setBranchFilterIds}
          />
          {/* Live search — filters full_name, username, and email
              across every group (Management, Care Team, Archived).
              Empty input restores the full roster instantly. */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              name="user-roster-search"
              id="user-roster-search"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email"
              className="h-9 w-64 pl-8 pr-8 text-sm"
              data-testid="user-search-input"
              aria-label="Search users"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="user-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-staff-btn"><Plus className="w-4 h-4 mr-1" /> New login</Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Strict mutual exclusivity: Active sections are shown ONLY
         *  when the toggle is OFF; the Archived section is shown ONLY
         *  when it's ON. This is what the operators asked for after
         *  the Feb 8 audit — no more mixed views. */}
        {!showInactive && (
          <>
            <SectionCard
              title="Active Management Team"
              icon={<ClipboardList className="w-4 h-4 text-primary" />}
              desc="Admin, Manager, Reservation, Payment — do not appear on the calendar"
              testid="section-management"
              list={groups.management}
              renderRows={(l) => renderRows(l, true)}
              showReportColumn={canManageReportPerms}
            />
            <SectionCard
              title="Active Care Team"
              icon={<Stethoscope className="w-4 h-4 text-primary" />}
              desc="Doctors, Nurses, Therapists — appear on the calendar"
              testid="section-care-team"
              list={groups.careTeam}
              renderRows={(l) => renderRows(l, false)}
              showReportColumn={false}
            />
          </>
        )}

        {showInactive && (
          <ArchivedSectionCard
            list={groups.archived}
            isOwner={isOwner(staff)}
            onPurge={() => setPurgeOpen(true)}
            renderRows={(l) => renderRows(l, false)}
          />
        )}
      </div>

      {/* Global Brand-level staff registry wizard (2-step, role-aware).
       *  Used for BOTH create AND edit flows — passing `editing={u}` re-uses
       *  the same Identity + Specialization steps for updates.
       */}
      <BrandStaffWizard
        open={wizardOpen}
        onOpenChange={(o) => { setWizardOpen(o); if (!o) setEditing(null); }}
        branches={branches}
        roles={rolesForAssignment}
        categories={categories}
        services={services}
        actorLevel={actorLevel}
        isActorOwner={isOwner(staff)}
        editing={editing}
        onSaved={load}
      />

      {/* Iter-205 — per-user Report Permissions overlay. Owner rows
       *  short-circuit inside the dialog to a read-only notice so
       *  they can't be locked out of a report. */}
      <ReportPermissionsDialog
        open={!!reportPermCtx}
        onOpenChange={(o) => { if (!o) setReportPermCtx(null); }}
        user={reportPermCtx?.user}
        scope={reportPermCtx?.scope}
      />

      {/* Owner-only bulk purge of every Inactive login. Confirmed with
       *  a dialog + explicit acknowledgement checkbox because deletion
       *  is permanent + cascades onto schedule rows. Backend enforces
       *  the Owner check again on `POST /api/admin/purge-inactive-staff`.
       */}
      <Dialog open={purgeOpen} onOpenChange={(o) => { setPurgeOpen(o); if (!o) setPurgeAck(false); }}>
        <DialogContent className="max-w-md" data-testid="purge-inactive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-800">
              <Trash2 className="w-4 h-4" /> Permanently delete inactive logins
            </DialogTitle>
            <DialogDescription>
              Are you sure? This will permanently erase these <span className="font-semibold" data-testid="purge-inactive-count">{groups.archived.length}</span> account{groups.archived.length === 1 ? "" : "s"} from the global database. This action cannot be undone.
              <br /><br />
              Email addresses become free for reuse. Appointment history for these people is <span className="font-medium">not</span> affected.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-sm text-rose-900 cursor-pointer">
            <Checkbox
              checked={purgeAck}
              onCheckedChange={(v) => setPurgeAck(!!v)}
              data-testid="purge-inactive-ack"
              className="mt-0.5"
            />
            <span>I understand this cannot be undone.</span>
          </label>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPurgeOpen(false); setPurgeAck(false); }} disabled={purging} data-testid="purge-inactive-cancel">Cancel</Button>
            <Button
              className="bg-rose-700 hover:bg-rose-800 text-white"
              disabled={purging || !purgeAck}
              data-testid="purge-inactive-confirm"
              onClick={async () => {
                setPurging(true);
                try {
                  const r = await staffApi.post("/admin/purge-inactive-staff");
                  toast.success(`${r.data.deleted_count} inactive login${r.data.deleted_count === 1 ? "" : "s"} deleted.`);
                  setPurgeOpen(false);
                  setPurgeAck(false);
                  await load();
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setPurging(false);
                }
              }}
            >
              {purging ? "Deleting…" : `Delete ${groups.archived.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate dialog — restores login + branches */}
      <Dialog open={!!reactivateCtx} onOpenChange={(o) => { if (!o) setReactivateCtx(null); }}>        <DialogContent className="max-w-md" data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle>Reactivate staff login</DialogTitle>
            <DialogDescription>
              {reactivateCtx && <>Select the branches <b>{reactivateCtx.user.full_name}</b> will work at. The login will be restored instantly.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-auto">
            {branches.map((b) => {
              const on = reactivateCtx?.branch_ids?.has(b.id);
              return (
                <label key={b.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`reactivate-branch-${b.id}`}>
                  <Checkbox
                    checked={on}
                    onCheckedChange={(v) => setReactivateCtx((c) => {
                      if (!c) return c;
                      const next = new Set(c.branch_ids);
                      if (v) next.add(b.id); else next.delete(b.id);
                      return { ...c, branch_ids: next };
                    })}
                    data-testid={`reactivate-branch-check-${b.id}`}
                  />
                  <span className="text-sm">{b.name}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReactivateCtx(null)} data-testid="reactivate-cancel">Cancel</Button>
            <Button
              onClick={submitReactivate}
              disabled={!reactivateCtx || reactivateCtx.branch_ids.size === 0}
              data-testid="reactivate-confirm"
            >Restore login</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
